"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CircleCheck, ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const servicesData = [{
  title: "Digital Transformation",
  description: "Modernizing your core operations with cloud-native architectures and seamless digital workflows to drive efficiency.",
  features: ["Legacy system modernization", "Cloud infrastructure optimization", "Digital workflow automation"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688832/img/digital_transformation_abstract.png",
  link: "/StrategicPillarsPage"
}, {
  title: "Business Strategy",
  description: "Data-driven roadmaps designed to navigate market complexities and unlock sustainable competitive advantages.",
  features: ["Market disruption analysis", "Strategic growth mapping", "Operational excellence frameworks"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688832/img/business_strategy_nodes.png",
  link: "/StrategicPillarsPage"
}, {
  title: "Artificial Intelligence",
  description: "Integrating advanced machine learning and generative AI to automate decision-making and personalize customer experiences.",
  features: ["Custom LLM implementation", "Predictive analytics engines", "AI-driven process automation"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688832/img/ai_neural_network.png",
  link: "/StrategicPillarsPage"
}, {
  title: "Financial Advisory",
  description: "Precision financial modeling and risk management strategies to ensure long-term capital stability and growth.",
  features: ["Capital structure optimization", "Risk mitigation strategies", "Investment performance tracking"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688832/img/financial_data_visualization.png",
  link: "/StrategicPillarsPage"
}, {
  title: "Growth Marketing",
  description: "High-impact digital strategies that combine creative storytelling with technical precision to scale your brand globally.",
  features: ["Performance marketing at scale", "Brand authority positioning", "Conversion rate optimization"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688832/img/growth_marketing_flow.png",
  link: "/StrategicPillarsPage"
}];
function ServiceBlock({
  service,
  index
}: {
  service: typeof servicesData[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const isReversed = index % 2 !== 0;
  return <div ref={ref} data-index={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className={`relative w-full aspect-square max-w-md mx-auto lg:max-w-none ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-secondary/10 to-primary/5 rounded-3xl transform rotate-2 scale-105 -z-10 transition-transform duration-700 hover:rotate-3 hover:scale-105"></div>
        <div className="absolute inset-0 bg-card/60 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl -z-10"></div>
        <div className="h-full w-full flex items-center justify-center p-8">
          <img src={service.image} alt={`Future Mind Consulting: ${service.title}`} className="w-full h-full object-contain drop-shadow-[0_0_30px_rgba(0,255,255,0.3)] animate-in fade-in zoom-in duration-1000" loading="lazy" />
        </div>
      </div>

      {}
      <div className={`flex flex-col gap-8 ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
        <div className="space-y-4">
          <div className="inline-flex items-center rounded-md border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-mono uppercase tracking-widest text-primary">
            <span className="flex h-1.5 w-1.5 rounded-lg bg-primary mr-2 animate-pulse"></span>
            Solution {index + 1}
          </div>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            {service.title}
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            {service.description}
          </p>
        </div>

        <ul className="space-y-4">
          {service.features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-3">
              <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-1" />
              <span className="text-foreground font-default font-medium">{feature}</span>
            </li>)}
        </ul>

        <div className="pt-4">
          <Button asChild size="lg" className="group rounded-md px-8 bg-primary text-primary-foreground hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all duration-300">
            <Link to={service.link}>
              Explore Solution
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function CoreSolutions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224262" id="core-solutions" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 lg:mb-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-foreground mb-6">
            Engineering the <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Future of Business</span>
          </h2>
          <p className="text-xl text-muted-foreground font-default">
            Future Mind Consulting delivers high-end strategic frameworks designed to scale innovation and operational intelligence.
          </p>
        </div>

        {}
        <div className="flex flex-col gap-24 lg:gap-40">
          {servicesData.map((service, index) => <ServiceBlock key={index} service={service} index={index} />)}
        </div>

      </div>
    </section>;
}